import { QualifiedId, QualifiedIdVector } from "@imi-ernd-be/hyselect-engine";
import { isNotNil } from "@src/shared/helpers/guards";
import { isFunction, isString } from "radashi";
import { engine } from ".";
import { Deletable } from "./helpers/withDelete";

export type EngineVector<T = unknown> = {
  get(index: number): T;
  resize(count: number, value: T): void;
  set(index: number, value: T): boolean;
  size(): number;
} & Deletable;

class VectorWrapper<T> implements ArrayLike<T> {
  readonly [n: number]: T;

  get length(): number {
    return this.vector.size();
  }

  private constructor(private readonly vector: EngineVector<T>) {}

  static from<T>(vector: EngineVector<T>): VectorWrapper<T> {
    const handler: ProxyHandler<VectorWrapper<T>> = {
      get(target, prop, receiver: unknown) {
        if (isString(prop)) {
          const index = parseInt(prop);

          if (!isNaN(index)) {
            return target.get(index);
          }
        }

        return Reflect.get(target, prop, receiver);
      },
    };

    return new Proxy(new VectorWrapper(vector), handler);
  }

  get(index: number) {
    return this.vector.get(index);
  }
}

export function createQualifiedIdVector(
  array: QualifiedId[],
): QualifiedIdVector {
  const vector = new engine.QualifiedIdVector();

  for (const item of array) {
    vector.push_back(item);
    // push_back creates a copy of the object, so we can safely call delete on the original
    // object without affecting the one in the vector
    item.delete();
  }

  return vector;
}

export function isVector<T>(value: unknown): value is EngineVector<T> {
  return (
    typeof value === "object" &&
    value !== null &&
    "size" in value &&
    isFunction(value.size) &&
    "get" in value &&
    isFunction(value.get)
  );
}

export function toArray<T>(
  vector: EngineVector<T>,
  { ignoreDelete = false } = {},
): NonNullable<T>[] {
  try {
    return Array.from(VectorWrapper.from(vector)).filter(isNotNil);
  } finally {
    if (!ignoreDelete) {
      vector.delete();
    }
  }
}
